import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const CreateClass = () => {
  const [className, setClassName] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Generate random Class ID
  const generateUniqueId = (length = 5) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  };

  // Fetch sessions and classes on mount
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sessions/allSessions`);
        setSessions(res.data);
      } catch (err) {
        console.error('Error fetching sessions:', err);
      }
    };

    const fetchClasses = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses`);
        setClasses(res.data);
      } catch (err) {
        console.error('Error fetching classes:', err);
      }
    };

    fetchSessions();
    fetchClasses();

    // Check if sessionId is stored in localStorage and is not the same as current session
    const storedSessionId = localStorage.getItem('currentSessionId');
    if (storedSessionId && storedSessionId !== sessionId) {
      setSessionId(storedSessionId);
    }
  }, [sessionId]); 

  const handleSaveClass = async () => {
    if (!className || !sessionId) {
      alert("Class name and session are required");
      return;
    }

    const newClassId = generateUniqueId();
    setLoading(true);
    try {
      const newClass = { classId: newClassId, className, sessionId };
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/classes/createClass`,
        newClass
      );

      setClasses([...classes, response.data]);
      setClassName('');
      setSessionId('');
      alert('Class created successfully!');
    } catch (error) {
      console.error('Error creating class:', error);
      alert(error.response?.data?.message || 'Error creating class');
    }finally {
    setLoading(false); 
  }
  };

  const handleDeleteClass = async (id) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/classes/deleteClassById/${id}`);
        setClasses(classes.filter((item) => item.classId !== id));
        alert('Class deleted successfully!');
      } catch (error) {
        alert('Error deleting class');
      }
    }
  };

  // Filter classes based on selected session
 const filteredClasses = sessionId
  ? classes.filter((cls) => cls.sessionId?._id === sessionId)
  : classes;

  const handleSessionChange = (e) => {
    const newSessionId = e.target.value;
    setSessionId(newSessionId);

    // Check if the session is already stored in localStorage, and only set if different
    if (newSessionId !== localStorage.getItem('currentSessionId')) {
      // Store the selected session in localStorage
      localStorage.setItem('currentSessionId', newSessionId);
    }
  };

  return (
    <div className="p-6 bg-white shadow-md rounded-lg max-w-md mx-auto min-w-full">
      <div className="mb-4">
        <label className="block text-black font-semibold text-[18px] mb-2">Class Name</label>
        <input
          type="text"
          placeholder="Enter class name"
          value={className}
          onChange={(e) => setClassName(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-800"
        />
      </div>

      <div className="mb-4">
        <label className="block text-black font-semibold text-[18px] mb-2">Session</label>
        <select
          value={sessionId}
          onChange={handleSessionChange} // Handle session change
          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-800"
        >
          <option value="">Select Session</option>
          {sessions.map((s) => (
            <option key={s._id} value={s._id}>
              {s.sessionName}
            </option>
          ))}
        </select>
      </div>

      <div className="flex justify-center mb-6">
        <button
  onClick={handleSaveClass}
  disabled={loading}
  className={`w-1/2 md:w-1/4 px-4 py-2 text-white font-semibold rounded-lg 
    bg-gradient-to-r from-blue-500 to-blue-700 transition
    ${loading ? "opacity-70 cursor-not-allowed" : "hover:opacity-90"}`}
>
  {loading ? "Creating..." : "Save Class"}
</button>

      </div>

      <h2 className="font-bold mb-4 text-gray-800 text-[18px]">Class List</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg text-center">
          <thead className="bg-gray-200 border-b border-gray-300">
            <tr>
              <th className="px-4 py-2 border-b font-semibold text-black text-[18px]">Sr. No.</th>
              <th className="px-4 py-2 border-b font-semibold text-black text-[18px]">Class Name</th>
              <th className="px-4 py-2 border-b font-semibold text-black text-[18px]">Class ID</th>
              <th className="px-4 py-2 border-b font-semibold text-black text-[18px]">Session</th>
              <th className="px-4 py-2 border-b font-semibold text-black text-[18px]">Action</th>
            </tr>
          </thead>

          <tbody className="bg-gray-50">
            {filteredClasses.map((item, index) => (
              <tr key={item.classId} className="border-b border-gray-300 hover:bg-gray-100">
                <td className="px-4 py-2 text-gray-700">{index + 1}</td>
                <td className="px-4 py-2 text-gray-700">{item.className}</td>
                <td className="px-4 py-2 text-gray-700">{item.classId}</td>
                <td className="px-4 py-2 text-gray-700">{item.sessionId?.sessionName}</td>
                <td className="px-4 py-2 flex justify-center gap-2">
                  <Link
                    to={`/classes/${item.classId}`}
                    className="bg-green-600 hover:bg-green-500 text-white py-1 px-3 rounded flex items-center text-[14px]"
                  >
                    View
                  </Link>
                  <button
                    onClick={() => handleDeleteClass(item.classId)}
                    className="bg-red-500 hover:bg-red-400 text-white py-1 px-3 rounded flex items-center text-[14px]"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {filteredClasses.length === 0 && (
              <tr>
                <td colSpan="5" className="py-4 text-gray-500">No classes for this session.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CreateClass;
